import React, { useEffect } from "react";

import { trackMixpanel, useAppType, useGeneralContext, useLocation, useTranslation, useUserContext } from "utils";

import { Crumb, ErrorBoundary, InformationCard, MetaData, PageSpin, Share } from "components";

export type NavigationCrumbs = {
  crumbs: Crumb[];
  additionalData?: React.ReactNode;
};

type MainPageTypes = {
  pageName: string;
  share?: Share;
  afterPage?: React.ReactNode;
  beforePage?: React.ReactNode;
  children: React.ReactNode;
  containerClass?: string;
  fullWidth?: boolean;
  loading?: boolean;
  dataTestId?: string;
  className?: string;
  navigationCrumbs?: NavigationCrumbs;
  showMobileAlert?: boolean;
};

export const MainPage = ({
  afterPage,
  beforePage,
  children,
  containerClass = "",
  fullWidth,
  loading,
  pageName,
  share,
  dataTestId,
  className,
  navigationCrumbs = { crumbs: [], additionalData: <></> },
  showMobileAlert,
}: MainPageTypes) => {
  const location = useLocation();
  const { user } = useUserContext();
  const { t } = useTranslation(["meta"]);
  const { mobile } = useAppType();
  const { appNavSideCollapsed, setFullWidthPage, setNavigationCrumbs } = useGeneralContext();

  useEffect(() => {
    trackMixpanel("OpenPage", { "page url": window.location.pathname });
  }, [window.location.pathname]); // eslint-disable-line

  useEffect(() => {
    if (!navigationCrumbs.crumbs.length) return;
    setNavigationCrumbs?.(navigationCrumbs);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    // Cleanup function that runs on unmount
    return () => {
      setNavigationCrumbs?.({ crumbs: [], additionalData: <></> });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!setFullWidthPage || fullWidth === undefined) return;
    setFullWidthPage(!!fullWidth);

    return () => {
      if (setFullWidthPage) setFullWidthPage(false);
    };
  }, [fullWidth, setFullWidthPage]);

  const classNames = `${fullWidth ? "full-width-main-page" : "container"} ${appNavSideCollapsed ? "" : "menu-open"} content ${user ? "" : "anon-page"} ${
    user || containerClass.includes("margin-top-0") ? "" : "margin-top-250"
  } ${containerClass || ""}`;

  const pageContent = mobile && showMobileAlert ? <MobileAlert /> : children;

  return (
    <>
      <MetaData
        title={share?.title ? share.title : t("default.title")}
        description={share?.summary ? share.summary : t("default.description")}
        image={share?.image ? share.image : `${process.env.REACT_APP_FARM_URL}/images/misc/share.png`}
        link={`${process.env.REACT_APP_FARM_URL}${location.pathname}`}
      />
      {beforePage}
      <section className={`page ${pageName} ${user && mobile ? "in-app" : ""}`} data-testid={dataTestId}>
        <section className={className ?? classNames}>
          <ErrorBoundary>{!loading && children ? pageContent : <PageSpin />}</ErrorBoundary>
        </section>
      </section>
      {afterPage}
    </>
  );
};

const MobileAlert = () => {
  const { t } = useTranslation("global");

  return (
    <div className="padding-100">
      <InformationCard variant="neutral" title={t("mobileAlert.title")} text={t("mobileAlert.description")} iconName="tabler/info-circle" />
    </div>
  );
};
